import {fetch, XHRPromise, type FetchPreprocessor, type FetchPostprocessor, type FetchErrorPostprocessor, type FetchErrorHandler, type FetchOptions, type URLFetchOptions} from "./Fetch";

export class AjaxHandler {
    static _baseAjax: AjaxHandler | null = null;
    parentHandler: AjaxHandler | null;
    preprocessors: FetchPreprocessor[];
    postprocessors: FetchPostprocessor[];
    errorPostprocessors: FetchErrorPostprocessor[];
    errorHandler: FetchErrorHandler | null;

    constructor(ajaxHandler?: AjaxHandler | null, errorHandler: FetchErrorHandler | null = null) {
        if (this.constructor._baseAjax === null) {
            this.constructor._baseAjax = this;
        } else if (arguments.length === 0) {
            ajaxHandler = this.constructor._baseAjax;
        }
        this.parentHandler = ajaxHandler;
        this.preprocessors = (ajaxHandler) ? [] : Array.from(fetch.defaultPreprocessors);
        this.postprocessors = (ajaxHandler) ? [] : Array.from(fetch.defaultPostprocessors);
        this.errorPostprocessors = (ajaxHandler) ? [] : Array.from(fetch.defaultErrorPostprocessors);
        this.errorHandler = errorHandler;
    }

    fetch(request: RequestInfo | URLFetchOptions, ...args: FetchOptions[]): XHRPromise | undefined {
        if (!request) {
            console.error("Missing request for fetch");
            return;
        }

        const baseOptions = {
            preprocessors: this.getPreprocessors(),
            postprocessors: this.getPostprocessors(),
            errorPostprocessors: this.getErrorPostprocessors(),
            errorHandler: this.getErrorHandler(),
        };

        // Request may be a plain object or a url, not going to duplicate code from fetch
        for (const key of Object.keys(baseOptions)) {
            if (typeof request === 'object' && request[key]) {
                delete baseOptions[key];
            }
        }

        return fetch(request, baseOptions, ...args);
    }

    request(request: RequestInfo, ...args: FetchOptions[]): XHRPromise | undefined {
        return this.fetch(request, ...args);
    }

    // Feel free to modify the post and get methods for your needs
    get(url: string, ...args: FetchOptions[]): XHRPromise | undefined {
        return this.fetch(url, ...args, {method: "GET"});
    }

    getJSON(url: string, data?: FetchOptions["data"], ...args: FetchOptions[]): XHRPromise | undefined {
        return this.get(url, {dataType: "json", data: data}, ...args);
    }

    post(url: string, ...args: FetchOptions[]): XHRPromise | undefined {
        return this.fetch(url, ...args, {method: "POST"});
    }

    postJSON(url: string, data?: FetchOptions["data"], ...args: FetchOptions[]): XHRPromise | undefined {
        return this.post(url, {dataType: "json", data: data}, ...args);
    }

    // Unlike postJSON, which sends a FormData, this sends the data itself as a JSON body
    postJSONBody(url: string, data?: FetchOptions["data"], ...args: FetchOptions[]): XHRPromise | undefined {
        return this.post(url, {dataType: "json", contentType: "application/json", data: data}, ...args);
    }

    addPreprocessor(preprocessor: FetchPreprocessor): void {
        this.preprocessors.push(preprocessor);
    }

    getPreprocessors(): FetchPreprocessor[] {
        const inherited = this.parentHandler?.getPreprocessors() || [];
        return [...this.preprocessors, ...inherited];
    }

    addPostprocessor(postprocessor: FetchPostprocessor): void {
        this.postprocessors.push(postprocessor);
    }

    getPostprocessors(): FetchPostprocessor[] {
        const inherited = this.parentHandler?.getPostprocessors() || [];
        return [...inherited, ...this.postprocessors];
    }

    addErrorPostprocessor(postprocessor: FetchErrorPostprocessor): void {
        this.errorPostprocessors.push(postprocessor);
    }

    getErrorPostprocessors(): FetchErrorPostprocessor[] {
        const inherited = this.parentHandler?.getErrorPostprocessors() || [];
        return [...inherited, ...this.errorPostprocessors];
    }

    getErrorHandler(): FetchErrorHandler | null | undefined {
        return this.errorHandler || this.parentHandler?.getErrorHandler();
    }
}

export class FixedURLAjaxHandler {
    url: string;
    ajax: AjaxHandler;

    constructor(url: string, ajaxHandler: AjaxHandler = Ajax, errorHandler: FetchErrorHandler | null = null) {
        this.ajax = new AjaxHandler(ajaxHandler, errorHandler);
        this.url = url;
    }

    get(...args: FetchOptions[]): XHRPromise | undefined {
        return this.ajax.get(this.url, ...args);
    }

    getJSON(data?: FetchOptions["data"], ...args: FetchOptions[]): XHRPromise | undefined {
        return this.ajax.getJSON(this.url, data, ...args);
    }

    post(...args: FetchOptions[]): XHRPromise | undefined {
        return this.ajax.post(this.url, ...args);
    }

    postJSON(data?: FetchOptions["data"], ...args: FetchOptions[]): XHRPromise | undefined {
        return this.ajax.postJSON(this.url, data, ...args);
    }
}

export const Ajax = new AjaxHandler();